import * as _ from 'lodash';

import {
  CVSSv2BaseMetricNames,
  CVSSv3BaseMetricNames,
  CVSSv4BaseMetricNames,
  getCVSSV2HumanReadableMetrics,
  getCVSSV3HumanReadableMetrics,
  getCVSSV4HumanReadableMetrics,
} from '../../../../../../shared/vulns/cvssUtils';
import { UNABLE_TO_CALCULATE } from '../../../../../../shared/vulns/remediation';
import { getHumanReadableEPSS } from '../../../../../utils';
import type { SelectedVuln } from '../../../../../../shared/organizationPackages/response';
import type { CVSSMetric } from '../../../../../../shared/vulns/types';

export const getSelectedVulnFirstWeakness = (selectedVuln?: SelectedVuln) => (
  selectedVuln ? _.head(selectedVuln.weaknesses) : null
);

export const getSelectedVulnTitle = (selectedVuln?: SelectedVuln) => (
  getSelectedVulnFirstWeakness(selectedVuln)?.title ?? 'General Vulnerability'
);

export const getSelectedVulnCvss = (selectedVuln?: SelectedVuln) => selectedVuln?.cvss ?? null;
export const getSelectedVulnCve = (selectedVuln?: SelectedVuln) => selectedVuln?.cve ?? null;
export const getExploitability = (selectedVuln?: SelectedVuln) => selectedVuln?.exploitability ?? null;

export const getSelectedVulnEpss = (selectedVuln?: SelectedVuln) => {
  if (!selectedVuln?.epss || !selectedVuln.epssPercentile) {
    return null;
  }

  return { score: selectedVuln.epss, percentile: selectedVuln.epssPercentile };
};

export const getSelectedVulnDescription = (selectedVuln?: SelectedVuln) => selectedVuln?.description ?? '';

export const getSelectedVulnDetails = (selectedVuln?: SelectedVuln) => {
  if (!selectedVuln) {
    return null;
  }

  return {
    cvss: selectedVuln.cvss ?? null,
    cve: selectedVuln.cve,
    cwes: selectedVuln.cwes,
    affectedVersionRanges: selectedVuln.affectedVersionRanges,
    patchedVersionRanges: selectedVuln.patchedVersionRanges,
    published: selectedVuln.published,
    cveStatus: selectedVuln.cveStatus,
    epss: !(selectedVuln.epss && selectedVuln.epssPercentile) ? null : getHumanReadableEPSS({
      score: selectedVuln.epss,
      percentile: selectedVuln.epssPercentile,
    }).label,
  };
};

export const getSelectedVulnRemediation = (selectedVuln?: SelectedVuln) => ({
  completeFix: selectedVuln?.completeFix ?? UNABLE_TO_CALCULATE,
  completeFixDistance: selectedVuln?.completeFixDistance,
  partialFix: selectedVuln?.partialFix ?? UNABLE_TO_CALCULATE,
  partialFixDistance: selectedVuln?.partialFixDistance,
});

export const getSelectedVulnNVDBaseMetrics = (selectedVuln?: SelectedVuln): CVSSMetric[] => {
  const metrics: CVSSMetric[] = [];

  const appendMetrics = (
    values: Record<string, string | undefined> | null,
    names: Record<string, { title: string }>,
  ) => {
    Object.entries(values ?? {}).forEach(([shortname, value]) => {
      const details = names[shortname];
      if (details && value) {
        metrics.push({ name: details.title, value });
      }
    });
  };

  if (selectedVuln?.cvssV4Vector) {
    appendMetrics(getCVSSV4HumanReadableMetrics(selectedVuln.cvssV4Vector), CVSSv4BaseMetricNames);
  } else if (selectedVuln?.cvssV3Vector) {
    appendMetrics(getCVSSV3HumanReadableMetrics(selectedVuln.cvssV3Vector), CVSSv3BaseMetricNames);
  } else if (selectedVuln?.cvssV2Vector) {
    appendMetrics(getCVSSV2HumanReadableMetrics(selectedVuln.cvssV2Vector), CVSSv2BaseMetricNames);
  }

  return metrics;
};

export const getSelectedVulnReferences = (selectedVuln?: SelectedVuln) => selectedVuln?.references ?? [];
